import { AppBar, Grid, Link, Paper, Toolbar, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useParams, Link as RouterLink } from 'react-router-dom';
import NavigationCategories from '../common/NavigationCategories';
import NotFoundIcon from '@mui/icons-material/Dangerous';
import LoginIcon from '@mui/icons-material/LockOutlined';
import SignUpIcon from '@mui/icons-material/AddCircle';
import HelpIcon from '@mui/icons-material/Help';

const Styles = {
    paper: {
        maxWidth: { xl: 1240, lg: 940, md: 640, sm: 480, xs: 320 },
        width: { xl: 1200, lg: 900, md: 600, sm: 480, xs: 320 },
        margin: '20px auto',
        overflow: 'hidden',
        background: '#ededed'
    },
    contentWrapper: {
        margin: '40px 16px',
    },
};



const HelpObjects = (topic: string) => {
    switch (topic) {
        case 'top': return {
            title: NavigationCategories[0]?.view?.title,
            icon: NavigationCategories[0]?.icon,
            body: (
                <Typography>
                    The Top of the Mind is the core of The Pensieve Experience, here you will see your latest Thought in one of the three areas of life (Personal, Professional and Social). <br /><br />
                    In this page you can not only change the text of your Thought, but also its Topic, it's Area or Modify its associated links. Once you are done with a Thought, you can also delete it. <br /><br />
                    Remember to <strong>Always Save your Changes</strong>
                </Typography>
            )
        };
        case 'login': return {
            title: 'Login',
            icon: <LoginIcon />,
            body: (
                <Typography>
                    Use your Pensieve Account to Log into Your Pensieve and Access your Thoughts. <br /><br />
                    Page used to inform your Credentials and Authenticate with The Pensieve for access to Protected Content (such as your Thoughts and other Private Information) <br /><br />
                    Don't have an account? <Link component={RouterLink} to={"/signup"} > Sign Up! </Link> <br /><br />
                </Typography>
            )
        };
        case 'signup': return {
            title: 'Sign Up',
            icon: <SignUpIcon />,
            body: (
                <Typography>
                    Create a Pensieve Account so you can Start Calming your Brain Storm and keep Important Thoughts always close. <br /><br />
                    Page used to create a new Account in The Pensieve. You only need a Unique Username and a Password to Sign Up, and don't worry, we will let you know if the username has been taken. <br /><br />
                </Typography>
            )
        };
        case 'help': return {
            title: 'Help',
            icon: <HelpIcon />,
            body: (
                <Typography>
                    Even though Pensieve is an Easy and Simple Tool, it comes with a Help to guide you. <br /><br />
                    Click the Help button in any page while using The Pensieve and you will be taken to the Help Page for that Topic. <br /><br />
                </Typography>
            )
        };
        case 'profile': return {
            title: NavigationCategories[1]?.children[0]?.view?.title,
            icon: NavigationCategories[1]?.children[0]?.icon,
            body: (
                <Typography>
                    In order to enjoy a more personalized experience while browsing your Pensieve, additional information can be provided as part of your Profile. <br /><br />
                    In this page you can change your Name and also what Avatar you want to use. To leverage a more uniform approach for Avatar Storage and Management, we use <a href='https://www.gravatar.com' target={'_blank'}>Gravatar</a>.<br /><br />
                </Typography>
            )
        };
        case 'settings': return {
            title: NavigationCategories[1]?.children[1]?.view?.title,
            icon: NavigationCategories[1]?.children[1]?.icon,
            body: (
                <Typography>
                    While a User Profile refers to additional information, his Settings deal with how The Pensieve is set to Operate. <br /><br />
                    In this page you can change Defaults used by The Pensieve and adjust how and with which frequency you are Notified by the system (Currently Pending).<br /><br />
                </Typography>
            )
        };
        case 'topic': return {
            title: NavigationCategories[2]?.children[0]?.view?.title,
            icon: NavigationCategories[2]?.children[0]?.icon,
            body: (
                <Typography>
                    Topics are used in the Pensieve to better organize your Thoughts. Instead of relying on unstructured information, The Pensieve organize Topics in a Graph with total customization power to each user. <br /><br />
                    In this page you can author or modify a Topic, this is done by providing a short Summary that describes the Topic and also indicating Topics that are Associated to this one and what is the type of association. <br /><br />
                    Remember to <strong>Always Save your Changes</strong> (Especially after modifying its Associations as nothing is Auto-Saved).
                </Typography>
            )
        };
        case 'explore': return {
            title: NavigationCategories[2]?.children[1]?.view?.title,
            icon: NavigationCategories[2]?.children[1]?.icon,
            body: (
                <Typography>
                    Since Topics are the main Organizing Tool, it's useful to Explore the existing Topics sometimes to see what is a good Subject for the day. <br /><br />
                    In this page you can explore existing Topics and any provided summary, in this page you can also Create a new Topic or Remove your Customizations from one (Effectively removing Topic if you are the only one using it). <br /><br />
                    <strong>TOPIC REMOVAL IS A PENDING FEATURE AT THE MOMENT</strong>
                </Typography>
            )
        };
        case 'mind': return {
            title: NavigationCategories[3]?.children[0]?.view?.title,
            icon: NavigationCategories[3]?.children[0]?.icon,
            body: (
                <Typography>
                    Thoughts are the Heart of the Pensieve, here is where you find all Thoughts you have including those that are not currently in the Top of your Mind. <br /><br />
                    In this page you can revisit Thoughts and modify them by changing their Text, Topic or Area which will then move them back to the Top of the Mind. <br /><br />
                    Remember to <strong>Always Save your Changes</strong>.
                </Typography>
            )
        };
        case 'timeline': return {
            title: NavigationCategories[3]?.children[1]?.view?.title,
            icon: NavigationCategories[3]?.children[1]?.icon,
            body: (
                <Typography>
                    Keep track of your Story with the help of the Timeline, allowing a look back at how your Thoughts changed over time. <br /><br />
                    In this page you can see all changes in your Pensieve organized in a Timeline. <br /><br />
                </Typography>
            )
        };
        default: return { 
            title: 'Topic Not Found', 
            icon: <NotFoundIcon />, 
            body: 'No Information Available' 
        };
    }
};

function HelpPage() {
    const params = useParams();

    let uiHelp = HelpObjects( params?.page || 'top' );

    return (
        <Paper sx={Styles.paper}>
            <AppBar position='static' color='default' elevation={0}>
                <Toolbar>
                    <Grid container spacing={2} alignItems='center'>
                        <Grid item> {uiHelp.icon} </Grid>
                        <Grid item> <Typography> {uiHelp.title} </Typography> </Grid>
                    </Grid>
                </Toolbar>
            </AppBar>
            <Box sx={Styles.contentWrapper} >
                {uiHelp.body}
            </Box>
        </Paper>
    )
}

export default HelpPage